import { useQueryClient } from '@tanstack/react-query';
import {
  ArchiveRestore,
  ArrowLeft,
  Check,
  ChevronDown,
  Columns3,
  Ellipsis,
  Filter,
  Globe,
  Group,
  KeyRound,
  Lock,
  Pencil,
  Pin,
  PinOff,
  Plus,
  RefreshCw,
  SquarePen,
  Tags,
  Vault,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useApi } from '../api/context';
import { queryKeys, useSecrets } from '../api/queries';
import type {
  BlueprintDocument,
  Deployment,
  ParameterDefinition,
  ResourceType,
  SecretMetadata,
  SettingDelivery,
  SettingLocation,
} from '../api/types';
import { RecentActivity } from '../activity/ActivityTimeline';
import type { MappedDiagnostic } from '../lib/diagnostics';
import { useAuth, usePermission } from '../auth/context';
import { useToast } from '../components/toast';
import { ActionButton, ErrorMessage } from '../components/ui';
import { formatDateTime, formatRelative } from '../lib/format';
import { usePhone } from '../shell/breakpoints';
import { MenuButton, type MenuEntry } from '../shell/Menu';
import { strings } from '../strings';
import { useSecretActions } from './actions';
import { AppDetailsPane, AppStatusChip, AppStrip } from './AppStatus';
import {
  appDelivery,
  appStatusParts,
  appStatusText,
  rollingOutSince,
  useBoundedPolling,
} from './appStatusModel';
import { ConfirmDialog } from '../deployments/runDialogs';
import { isRollingOut } from './useVariablesData';
import { DeclarationsEditor } from './DeclarationsEditor';
import type { DeclarationsEditing } from './declarations';
import { DetailsPane } from './DetailsPane';
import { rangeBetween } from './grid';
import {
  copyableValue,
  secretStatuses,
  secretValueCell,
  SETTING_NAME,
  STATUS_ORDER,
  valueCell,
  type StatusId,
  type ValueCell,
  type VariableApp,
  type VariableRow,
} from './model';
import { ParameterEditor } from './ParameterEditor';
import { deliveryEntry, locationEntry, managedVaultEntry } from './registry';
import { RevealContext, rolesText, type RevealEnvironment } from './reveal';
import { useRevealCopy, type CopyTarget } from './useRevealCopy';
import { SecretsView } from './SecretsView';
import { SetValuePopover } from './SetValuePopover';
import { SourceTypesDialog } from './SourceTypesDialog';
import type { VariablesData, VariablesScope } from './useVariablesData';
import { useVariablesView } from './useVariablesView';
import { InlineInput, VariableForm, type VariableEdit } from './VariableForm';
import { VariablesTable, type RowCallbacks } from './VariablesTable';
import type { VariableRef } from './varParam';
import { formatVarParam } from './varParam';
import {
  effectiveGroup,
  filterRows,
  groupLines,
  moveColumn,
  nextSort,
  NO_FILTERS,
  offeredColumns,
  sortRows,
  visibleColumns,
  type ColumnId,
  type Filters,
  type GroupBy,
  type KindFilter,
} from './view';

const s = strings.variables;
const TABLE_MIN_WIDTH = 760;
const EMPTY: ReadonlySet<string> = new Set();

/** Design-scope edits of the open blueprint (spec 6.10.10); each is one undo step of the designer. */
export interface VariablesEditing {
  /** Adds or replaces a variable (with a declaration added in the same step). */
  set(edit: VariableEdit): void;
  /** Sets a variable's value (inline edit). */
  setValue(resourceId: string, key: string, value: string): void;
  rename(resourceId: string, from: string, to: string): void;
  remove(items: readonly { resourceId: string; key: string }[]): void;
}

export interface VariablesWindowProps {
  context: 'designer' | 'deployment' | 'run';
  data: VariablesData;
  types: ReadonlyMap<string, ResourceType>;
  blueprintId: string;
  deployment: Deployment | null;
  scope: VariablesScope;
  /** The scope in effect (designer: the selection unless pinned). */
  effectiveScope: string[] | null;
  onScopeChange: (scope: VariablesScope) => void;
  /** Designer: the open document (editing, the design Secrets view). */
  doc?: BlueprintDocument | null;
  designVersion?: number | null;
  editing?: VariablesEditing | null;
  /** A row to show (?var=, diagnostics); handled once the rows are there. */
  pending?: { ref: VariableRef; nonce: number } | null;
  onPendingHandled?: () => void;
  /** A search to set (the Triggers tab's "Show n settings", feature spec 16 9.5); each nonce applies once. */
  pendingSearch?: { text: string; nonce: number } | null;
  /** Run page: the resources of the run. */
  runResources?: readonly string[] | null;
  highlightedSecret?: string | null;
  onHighlightSecret?: (name: string | null) => void;
  onSelectResource?: (resourceId: string) => void;
  onSelectConnection?: (key: string) => void;
  onAddDeclaration?: (name: string) => void;
  /** The blueprint's latest version's parameter declarations (the parameter edit, spec 6.10.11). */
  parameters?: Record<string, ParameterDefinition> | null;
  /** Designer: the secret declarations editor of the Secrets view (spec 6.10.13); null = read-only. */
  declarations?: DeclarationsEditing | null;
  /** Diagnostics at `/secrets/<name>`, shown with the declarations. */
  secretDiagnostics?: ReadonlyMap<string, MappedDiagnostic[]>;
  /** A declaration to show and focus (a `/secrets/<name>` problem was chosen). */
  pendingDeclaration?: { name: string; nonce: number } | null;
  /** A deployment secret to show with its details (an activity target, spec 6.11.3). */
  pendingSecret?: { name: string; nonce: number } | null;
}

type EditingCell = { rowId: string; column: 'variable' | 'value' } | null;
type PopoverTarget = { secret: SecretMetadata; row: VariableRow | null; anchor: HTMLElement | null } | null;

function useWidth(element: HTMLElement | null): number {
  const [width, setWidth] = useState(1200);
  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 1200));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return width;
}

function chip(label: string, value: string, active: boolean) {
  return (
    <>
      <Filter aria-hidden className="size-3.5" />
      <span>{s.filter.chip(label, value)}</span>
      <ChevronDown aria-hidden className="size-3" />
      {active && <span className="sr-only">(active)</span>}
    </>
  );
}

/**
 * The Variables tool window (spec 6.10): scope, the Variables and Secrets views, filters, the table with its
 * keyboard, details, bulk actions, Set value, and design-scope editing.
 */
export function VariablesWindow(props: VariablesWindowProps) {
  const { data, deployment, context, types, blueprintId, editing } = props;
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canOperate = usePermission('operate');
  const canDesign = usePermission('design');
  const canReveal = usePermission('reveal-secrets');
  const { me } = useAuth();
  const canWrite = canOperate || canDesign;
  const [settings, updateSettings] = useVariablesView();
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const width = useWidth(root);
  // Phones get cards in the bottom sheet whatever its width (spec 6.10.16, m12).
  const phone = usePhone();
  const layout: 'table' | 'list' | 'cards' = phone ? 'cards' : width >= TABLE_MIN_WIDTH ? 'table' : 'list';
  const narrow = layout !== 'table';
  const [filters, setFilters] = useState<Filters>(() => ({ ...NO_FILTERS, inRun: !!props.runResources }));
  // The bulk selection belongs to one view of one deployment.
  const selectionKey = `${settings.view}|${deployment?.id ?? ''}`;
  const [selectedState, setSelectedState] = useState<{ key: string; ids: ReadonlySet<string> }>(() => ({
    key: selectionKey,
    ids: new Set(),
  }));
  const selected: ReadonlySet<string> = selectedState.key === selectionKey ? selectedState.ids : EMPTY;
  const setSelected = (ids: ReadonlySet<string>) => setSelectedState({ key: selectionKey, ids });
  const [anchor, setAnchor] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [details, setDetails] = useState<string | null>(null);
  /** The app whose details are open (feature spec 15 19.3); open row details take their place. */
  const [appDetails, setAppDetails] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [form, setForm] = useState<{ mode: 'add' } | { mode: 'change'; rowId: string } | null>(null);
  const [popover, setPopover] = useState<PopoverTarget>(null);
  const [sourceTypes, setSourceTypes] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Kept here until the table handled it, then cleared: a table that mounts again (no rows while searching,
  // another view, Back from the details pane) never takes the focus again for an old request (code review C13).
  const [focusRequest, setFocusRequest] = useState<{ rowId: string; nonce: number } | null>(null);
  const focusNonce = useRef(0);
  const focusRow = (rowId: string) => setFocusRequest({ rowId, nonce: ++focusNonce.current });
  const clearFocusRequest = useCallback(() => setFocusRequest(null), []);
  /** After a removal: the given row, else the search field, so focus never falls back to the page. */
  const focusAfterRemoval = (next: VariableRow | null) => {
    if (next) focusRow(next.id);
    else requestAnimationFrame(() => search.current?.focus());
  };
  /** The parameter row whose value is being edited (spec 6.10.11). */
  const [parameterRow, setParameterRow] = useState<string | null>(null);
  /** Designer with an overlay: the Secrets view shows the declarations instead of the deployment's secrets. */
  const [declarationsMode, setDeclarationsMode] = useState(false);
  const search = useRef<HTMLInputElement>(null);
  const view = settings.view;
  const actions = useSecretActions(deployment?.id ?? null);
  const overview = useSecrets(deployment && view === 'secrets' ? deployment.id : null);
  const designMode = data.mode === 'design';
  const variables = data.variables;
  const editable = !!editing && canDesign;

  // ---------------------------------------------------------------- scope
  const scopeIds = props.effectiveScope;
  const scopeApps = useMemo(
    () => data.apps.filter((a) => !scopeIds || scopeIds.includes(a.resourceId)),
    [data.apps, scopeIds],
  );
  const scopeRows = useMemo(() => {
    const ids = new Set(scopeApps.map((a) => a.resourceId));
    return data.rows.filter((r) => ids.has(r.resourceId));
  }, [data.rows, scopeApps]);
  const runSet = useMemo(
    () => (props.runResources ? new Set(props.runResources) : null),
    [props.runResources],
  );

  // ---------------------------------------------------------------- per-app status, rollouts (feature spec 15)
  const pendingApps = scopeApps.filter((a) => a.appReferenceStatus?.neutral === 'pending');
  const pendingSince = pendingApps.length
    ? pendingApps
        .map((a) => a.lastRestart?.at ?? '')
        .sort()
        .pop() || 'pending'
    : null;
  const pollExpired = useBoundedPolling(deployment?.id ?? null, pendingSince, true);
  const [openedAt] = useState(() => Date.now());
  const [confirmRestart, setConfirmRestart] = useState<{
    app: string;
    label: string;
    disruption: string;
  } | null>(null);
  // The end of a rollout this user started is announced (a toast; the toast region is polite).
  useEffect(() => {
    for (const resourceId of actions.startedRollouts) {
      const app = data.apps.find((a) => a.resourceId === resourceId);
      const status = app?.appReferenceStatus;
      if (!app || !status || status.neutral === 'pending') continue;
      actions.forgetRollout(resourceId);
      if (status.neutral === 'resolved')
        toast.show({ tone: 'success', title: strings.vaults.rolledOut(resourceId) });
      else
        toast.show({
          tone: 'error',
          title: strings.vaults.rolloutFailed(resourceId, status.details ?? status.status),
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.apps]);

  // ---------------------------------------------------------------- rows, filters, lines
  const visibleRows = useMemo(
    () => sortRows(filterRows(scopeRows, data.statuses, filters, runSet), settings.sort, data.statuses),
    [scopeRows, data.statuses, filters, runSet, settings.sort],
  );
  const group: GroupBy = effectiveGroup(settings, scopeApps.length);
  const lines = useMemo(
    () => groupLines(visibleRows, group, scopeApps, collapsed),
    [visibleRows, group, scopeApps, collapsed],
  );
  // Opt-in reveal (spec 6.10.6, 6.10.18): on when `/me` grants it or any row or secret can be revealed.
  const revealOn =
    !!deployment &&
    (canReveal ||
      data.rows.some((r) => r.location.capabilities.reveal) ||
      (overview.data?.secrets ?? []).some((x) => x.location?.capabilities.reveal));
  const valueContext = useMemo(() => ({ ...data.valueContext, revealOn }), [data.valueContext, revealOn]);
  const cells = useMemo(
    () => new Map<string, ValueCell>(scopeRows.map((r) => [r.id, valueCell(r, valueContext)])),
    [scopeRows, valueContext],
  );
  const rotationInScope = scopeRows.some((r) => !!r.location.rotation);
  const columns = visibleColumns(settings, { select: canWrite, rotation: rotationInScope });
  const rowById = useMemo(() => new Map(data.rows.map((r) => [r.id, r])), [data.rows]);

  // ---------------------------------------------------------------- pending row (?var=, diagnostics)
  // Handled while rendering once the rows are there (own state only); the page is told afterwards.
  const { pending, onPendingHandled } = props;
  const [handledPending, setHandledPending] = useState<number | null>(null);
  if (pending && !data.loading && pending.nonce !== handledPending) {
    setHandledPending(pending.nonce);
    const id = `${pending.ref.resourceId}/${pending.ref.name}`;
    const row =
      rowById.get(id) ??
      data.rows.find((r) => r.resourceId === pending.ref.resourceId && r.displayName === pending.ref.name);
    if (row) {
      setFilters((f) => ({ ...NO_FILTERS, inRun: f.inRun }));
      setCollapsed(new Set());
      setFocusRequest({ rowId: row.id, nonce: pending.nonce });
      setSelectedState({ key: `variables|${deployment?.id ?? ''}`, ids: new Set([row.id]) });
    }
  }
  useEffect(() => {
    if (!pending || handledPending !== pending.nonce) return;
    if (view !== 'variables') updateSettings((v) => ({ ...v, view: 'variables' }));
    onPendingHandled?.();
  }, [pending, handledPending, view, updateSettings, onPendingHandled]);

  // A search set from outside (the Triggers tab): the Variables view with only the search filter.
  const { pendingSearch } = props;
  const [handledSearch, setHandledSearch] = useState<number | null>(null);
  if (pendingSearch && pendingSearch.nonce !== handledSearch) {
    setHandledSearch(pendingSearch.nonce);
    setFilters((f) => ({ ...NO_FILTERS, inRun: f.inRun, search: pendingSearch.text }));
  }
  useEffect(() => {
    if (pendingSearch && handledSearch === pendingSearch.nonce && view !== 'variables')
      updateSettings((v) => ({ ...v, view: 'variables' }));
  }, [pendingSearch, handledSearch, view, updateSettings]);

  // A `/secrets/<name>` problem: the Secrets view with the declarations, the name focused.
  const { pendingDeclaration } = props;
  const [handledDeclaration, setHandledDeclaration] = useState<number | null>(null);
  if (pendingDeclaration && pendingDeclaration.nonce !== handledDeclaration) {
    setHandledDeclaration(pendingDeclaration.nonce);
    setDeclarationsMode(true);
    setDetails(null);
  }
  useEffect(() => {
    if (pendingDeclaration && view !== 'secrets') updateSettings((v) => ({ ...v, view: 'secrets' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeclaration]);

  // An activity target (spec 6.11.3): the deployment's Secrets view with the secret's details.
  const { pendingSecret } = props;
  const [handledSecret, setHandledSecret] = useState<number | null>(null);
  if (pendingSecret && pendingSecret.nonce !== handledSecret) {
    setHandledSecret(pendingSecret.nonce);
    setDeclarationsMode(false);
    setDetails(`secret:${pendingSecret.name}`);
  }
  useEffect(() => {
    if (pendingSecret && view !== 'secrets') updateSettings((v) => ({ ...v, view: 'secrets' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSecret]);

  // ---------------------------------------------------------------- refresh (?fresh=true)
  const refresh = useCallback(async () => {
    if (!deployment) return;
    setRefreshing(true);
    try {
      // A load still in flight would overwrite the fresh answer with older data.
      await queryClient.cancelQueries({
        queryKey: view === 'secrets' ? queryKeys.secrets(deployment.id) : queryKeys.variables(deployment.id),
      });
      if (view === 'secrets') {
        const fresh = await api.getSecrets(deployment.id, undefined, true);
        queryClient.setQueryData(queryKeys.secrets(deployment.id), fresh);
        void queryClient.invalidateQueries({ queryKey: queryKeys.variables(deployment.id) });
      } else {
        const fresh = await api.getVariables(deployment.id, { fresh: true });
        queryClient.setQueryData(queryKeys.variables(deployment.id), fresh);
        void queryClient.invalidateQueries({ queryKey: queryKeys.secrets(deployment.id) });
      }
    } catch (error) {
      toast.error(error);
    } finally {
      setRefreshing(false);
    }
  }, [api, deployment, queryClient, toast, view]);

  // ---------------------------------------------------------------- selection
  // The rows the user can see (filters, scope and collapsed groups applied): selection and bulk actions only
  // ever count these (code review C20).
  const shownRows = lines.flatMap((l) => (l.kind === 'row' ? [l.row] : []));
  const selectionList = view === 'variables' ? shownRows.map((r) => r.id) : [];
  const select = (id: string, mode: 'toggle' | 'range', ordered: readonly string[] = selectionList) => {
    if (mode === 'range' && anchor && ordered.includes(anchor)) {
      const range = rangeBetween(ordered, ordered.indexOf(anchor), ordered.indexOf(id));
      setSelected(new Set([...selected, ...range]));
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    setAnchor(id);
  };

  // ---------------------------------------------------------------- secret helpers
  const vault = variables?.vault ?? overview.data?.vault ?? null;
  const vaultReady = vault?.status === 'ready';
  const account = vault?.readIdentity?.cloudAccountName ?? null;
  const deniedReason = strings.reveal.needsRole(rolesText(me.roles));
  const revealScopeKey = `${deployment?.id ?? ''}|${scopeIds?.join(',') ?? '*'}|${view}`;
  const revealEnv = useMemo<RevealEnvironment | null>(
    () =>
      deployment && revealOn
        ? {
            deploymentId: deployment.id,
            canReveal,
            deniedReason,
            account,
            user: me.id ?? me.name,
            scopeKey: revealScopeKey,
          }
        : null,
    [deployment, revealOn, canReveal, deniedReason, account, me.id, me.name, revealScopeKey],
  );
  const revealCopy = useRevealCopy(revealEnv);
  /** The value cell of a row (anchor of the reveal popovers; focusable in both layouts). */
  const valueCellOf = (row: VariableRow) =>
    root?.querySelector<HTMLElement>(
      `[data-row-id="${CSS.escape(row.id)}"] .var-col-value, li[data-row-id="${CSS.escape(row.id)}"]`,
    ) ?? null;
  const revealTarget = (cell: ValueCell | undefined): CopyTarget | null =>
    cell?.kind === 'secret-revealable'
      ? { secretName: cell.secretName, display: cell.display, location: cell.location }
      : null;
  /** Clicks a reveal control of the row's value cell (Enter, Ctrl+Enter and Ctrl+C reach them this way). */
  const clickReveal = (row: VariableRow, control: string): boolean => {
    const button = valueCellOf(row)?.querySelector<HTMLButtonElement>(`[${control}]`);
    if (!button) return false;
    button.click();
    return true;
  };
  const copySecretValue = (row: VariableRow): boolean => {
    if (clickReveal(row, 'data-reveal-copy')) return true;
    const target = revealTarget(cells.get(row.id));
    if (!target || !canReveal) return false;
    revealCopy.copy(target, valueCellOf(row));
    return true;
  };
  const secretValueCellOf = (name: string) =>
    root?.querySelector<HTMLElement>(`[data-testid="secret-${CSS.escape(name)}"] .var-col-value`) ?? null;
  const copySecretRowValue = (secret: SecretMetadata): boolean => {
    const element = secretValueCellOf(secret.name);
    const shown = element?.querySelector<HTMLButtonElement>('[data-reveal-copy]');
    if (shown) {
      shown.click();
      return true;
    }
    const target = secret.location
      ? revealTarget(secretValueCell(secret, secret.location, secret.kind, valueContext))
      : null;
    if (!target || !canReveal) return false;
    revealCopy.copy(target, element);
    return true;
  };
  const runActive = deployment?.activeRunId ? strings.deployments.runActive : null;
  const metadataOf = (row: VariableRow) => row.metadata;
  const openSetValue = (secret: SecretMetadata, row: VariableRow | null, element?: HTMLElement | null) => {
    const anchorElement =
      element ??
      root?.querySelector<HTMLElement>(
        row ? `[data-row-id="${CSS.escape(row.id)}"]` : `[data-testid="secret-${CSS.escape(secret.name)}"]`,
      ) ??
      null;
    setPopover({ secret, row, anchor: anchorElement });
  };
  const setValueButton = (secret: SecretMetadata, row: VariableRow | null, inline = false) => (
    <ActionButton
      permission="operate"
      variant={inline && !secret.drift.includes('deleted-outside') ? 'primary' : undefined}
      className="btn-sm"
      tabIndex={-1}
      disabledReason={vaultReady ? null : s.setValue.vaultFirst}
      aria-label={`${s.actions.setValue}: ${secret.name}`}
      icon={<KeyRound aria-hidden className="size-3.5" />}
      onClick={(event) =>
        openSetValue(secret, row, (event.currentTarget.closest('[data-row-id],tr') as HTMLElement) ?? null)
      }
    >
      {secret.drift.includes('deleted-outside') ? s.actions.setAgain : s.actions.setValue}
    </ActionButton>
  );
  const acceptButton = (name: string) => (
    <ActionButton
      permission="operate"
      className="btn-sm"
      tabIndex={-1}
      disabledReason={actions.busy ? strings.common.loading : null}
      aria-label={`${s.actions.accept}: ${name}`}
      icon={<Check aria-hidden className="size-3.5" />}
      onClick={() => void actions.accept(name)}
    >
      {s.actions.accept}
    </ActionButton>
  );
  // ---------------------------------------------------------------- maintenance (spec 6.10.11)
  const isParameterRow = (row: VariableRow) =>
    !!deployment &&
    row.scope === 'deployment' &&
    row.location.capabilities.setValue === 'deployment' &&
    !!row.location.name &&
    row.sync !== 'removed' &&
    props.parameters !== undefined;
  const editParameter = (row: VariableRow) => {
    setDetails(null);
    setParameterRow(row.id);
  };
  const parameterButton = (row: VariableRow) => (
    <ActionButton
      permission="operate"
      className="btn-sm"
      tabIndex={-1}
      aria-label={`${s.actions.editValue}: ${row.location.name}`}
      icon={<SquarePen aria-hidden className="size-3.5" />}
      onClick={() => editParameter(row)}
    >
      {s.actions.editValue}
    </ActionButton>
  );
  const canRecover = (secret: SecretMetadata | null, location?: SettingLocation | null): boolean =>
    !!secret &&
    secret.drift.includes('deleted-outside') &&
    !!(location ?? secret.location)?.capabilities.recover &&
    secret.recoverability !== 'not-recoverable';
  const recoverButton = (secret: SecretMetadata, inline = false) => {
    const busy = actions.isRecovering(secret.name);
    return (
      <ActionButton
        permission="operate"
        variant={inline ? 'primary' : undefined}
        className="btn-sm"
        tabIndex={-1}
        title={s.actions.recoverHelp}
        disabledReason={busy ? s.actions.recovering : null}
        aria-label={`${s.actions.recover}: ${secret.name}`}
        icon={
          busy ? (
            <RefreshCw aria-hidden className="size-3.5 animate-spin" />
          ) : (
            <ArchiveRestore aria-hidden className="size-3.5" />
          )
        }
        onClick={() => void actions.recover(secret.name)}
      >
        {busy ? s.actions.recovering : s.actions.recover}
      </ActionButton>
    );
  };
  const nowActionLabel = (resourceId: string, delivery: SettingDelivery | null) =>
    delivery ? (deliveryEntry(delivery).nowAction([resourceId], delivery)?.label ?? null) : null;
  const refreshButton = (
    resourceId: string,
    delivery: SettingDelivery | null,
    label?: string,
    short = false,
  ) => {
    const busy = actions.isRefreshing(resourceId);
    return (
      <ActionButton
        permission="operate"
        className="btn-sm"
        tabIndex={-1}
        title={deliveryEntry(delivery ?? { platform: null }).refreshText(resourceId)}
        disabledReason={busy ? s.actions.refreshingReferences : null}
        aria-label={label ?? `${s.actions.refreshReferences}: ${resourceId}`}
        icon={<RefreshCw aria-hidden className={`size-3.5 ${busy ? 'animate-spin' : ''}`} />}
        onClick={() => void actions.refreshReferences(resourceId, nowActionLabel(resourceId, delivery))}
      >
        {busy ? s.actions.refreshingReferences : short ? s.actions.refreshRefs : s.actions.refreshReferences}
      </ActionButton>
    );
  };
  const DANGER_REFERENCE = ['access-denied', 'not-found', 'invalid'];
  const needsRefresh = (row: VariableRow) =>
    !!deployment &&
    !!row.delivery?.refreshReferences &&
    !!row.referenceStatus &&
    DANGER_REFERENCE.includes(row.referenceStatus.neutral);
  /**
   * The rollout action of an app (feature spec 15 19.3): App Service restarts (confirmed), ECS rolls out new tasks,
   * Cloud Run deploys a new revision. Disabled while a run is active or the app is rolling out.
   */
  const rolloutOf = (app: VariableApp) => {
    if (!deployment || !app.applied) return null;
    const found = appDelivery(app, data.rows);
    if (!found) return null;
    const { delivery, entry } = found;
    const now = entry.nowAction([app.resourceId], delivery);
    if (!now) return null;
    const label = entry.nowActionShortLabel?.(app.resourceId) ?? now.label;
    const confirm = entry.nowActionConfirm ?? delivery.nowAction?.confirm ?? false;
    const busy = actions.isRestarting(app.resourceId);
    const disabledReason = deployment.activeRunId
      ? strings.vaults.rolloutBusy
      : isRollingOut(app)
        ? rollingOutSince(app)
        : busy
          ? strings.common.loading
          : null;
    const start = () =>
      void actions.restartApp(app.resourceId, {
        done: entry.nowActionDoneText?.([app.resourceId]) ?? label,
        failed: (error) => entry.nowActionFailedText?.(app.resourceId, error) ?? error,
      });
    const run = () =>
      confirm ? setConfirmRestart({ app: app.resourceId, label, disruption: now.disruption }) : start();
    return { label, busy, disabledReason, run };
  };
  const rolloutButton = (app: VariableApp, tabbable = false) => {
    const rollout = rolloutOf(app);
    if (!rollout) return null;
    const { label, busy, disabledReason, run } = rollout;
    return (
      <ActionButton
        permission="operate"
        className="btn-sm"
        tabIndex={tabbable ? undefined : -1}
        data-testid={`rollout-${app.resourceId}`}
        disabledReason={disabledReason}
        icon={<RefreshCw aria-hidden className={`size-3.5 ${busy ? 'animate-spin' : ''}`} />}
        onClick={run}
      >
        {label}
      </ActionButton>
    );
  };
  /** The group row's menu (Shift+F10): Details of <app> and the rollout. */
  const groupMenu = (app: VariableApp): MenuEntry[] => {
    if (!deployment || !appDelivery(app, data.rows)) return [];
    const entries: MenuEntry[] = [
      {
        kind: 'action',
        id: 'app-details',
        label: strings.vaults.appDetails(app.resourceId),
        hint: <span className="kbd">Alt ↵</span>,
        onSelect: () => openAppDetails(app.resourceId),
      },
    ];
    const rollout = rolloutOf(app);
    if (rollout)
      entries.push({
        kind: 'action',
        id: 'rollout',
        label: rollout.label,
        disabledReason: !canOperate
          ? strings.common.requiresRole('operator')
          : (rollout.disabledReason ?? undefined),
        onSelect: rollout.run,
      });
    return entries;
  };
  const groupStatus = (app: VariableApp) =>
    appStatusText(app, appDelivery(app, data.rows)?.entry ?? null)?.label ?? null;
  const openAppDetails = (resourceId: string) => {
    setDetails(null);
    setAppDetails(resourceId);
  };
  const groupAction = (app: VariableApp) => {
    if (!deployment || !app.applied) return null;
    const row = data.rows.find((r) => r.resourceId === app.resourceId && r.delivery?.refreshReferences);
    const found = appDelivery(app, data.rows);
    const rollout = rolloutButton(app);
    const refresh = row
      ? refreshButton(app.resourceId, row.delivery, s.maintenance.refreshApp(app.resourceId))
      : null;
    if (!rollout && !refresh && !app.appReferenceStatus) return null;
    return (
      <span className="flex items-center gap-1.5">
        {app.appReferenceStatus && <AppStatusChip app={app} entry={found?.entry ?? null} />}
        {refresh}
        {rollout}
      </span>
    );
  };
  const activityHref = (secret: string) =>
    deployment ? `/deployments/${deployment.id}?tab=activity&secret=${encodeURIComponent(secret)}` : '';

  const designerHref = (row: VariableRow) =>
    `/blueprints/${blueprintId}?${deployment ? `deployment=${deployment.id}&` : ''}var=${formatVarParam({ resourceId: row.resourceId, name: row.name })}`;
  const copy = (text: string, what: string) => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => toast.show({ tone: 'success', title: s.actions.copied(what) }))
      .catch(() => {});
  };

  // ---------------------------------------------------------------- row callbacks
  const canEditRow = (row: VariableRow) => editable && row.editable;
  const callbacks: RowCallbacks = {
    valueAction: (row) => {
      const m = metadataOf(row);
      const cell = cells.get(row.id);
      if (cell?.kind === 'deleted' && m && canRecover(m, row.location)) return recoverButton(m, true);
      if (!m || row.location.capabilities.setValue !== 'vault' || row.sync === 'removed') return null;
      if (cell?.kind === 'no-value' || cell?.kind === 'deleted') return setValueButton(m, row, true);
      return null;
    },
    primaryAction: (row) => {
      const m = metadataOf(row);
      const cell = cells.get(row.id);
      if (m && row.drift.includes('changed-outside')) return acceptButton(m.name);
      if (needsRefresh(row)) return refreshButton(row.resourceId, row.delivery, undefined, true);
      if (isParameterRow(row) && !canEditRow(row)) return parameterButton(row);
      if (cell?.kind === 'deleted' && canRecover(m, row.location)) return null;
      if (m && row.location.capabilities.setValue === 'vault' && row.sync !== 'removed') {
        if (cell?.kind === 'no-value' || cell?.kind === 'deleted') return null;
        return setValueButton(m, row);
      }
      if (canEditRow(row) && !row.secret)
        return (
          <button
            type="button"
            tabIndex={-1}
            className="btn btn-ghost btn-sm var-edit"
            aria-label={`${s.actions.edit}: ${row.displayName}`}
            onClick={() => editRow(row)}
          >
            <Pencil aria-hidden className="size-3.5" />
            {s.actions.edit}
          </button>
        );
      return null;
    },
    menu: (row) => rowMenu(row),
    activate: (row, column, key) => {
      if (column === 'variable' && canEditRow(row)) {
        setEditingCell({ rowId: row.id, column: 'variable' });
        return true;
      }
      if (column === 'value' || (column === 'variable' && key === 'F2')) {
        if (canEditRow(row)) {
          editRow(row);
          return true;
        }
        if (column === 'value' && isParameterRow(row) && canOperate) {
          editParameter(row);
          return true;
        }
        if (column === 'value' && key === 'Enter' && canReveal && revealTarget(cells.get(row.id)))
          return clickReveal(row, 'data-reveal-button');
        const m = metadataOf(row);
        if (
          m &&
          row.location.capabilities.setValue === 'vault' &&
          key === 'Enter' &&
          canOperate &&
          vaultReady
        ) {
          openSetValue(m, row);
          return true;
        }
      }
      if (column === 'actions' && key === 'Enter') {
        const button = root?.querySelector<HTMLButtonElement>(
          `[data-row-id="${CSS.escape(row.id)}"] .var-col-actions button:not([data-row-menu])`,
        );
        if (button) {
          button.click();
          return true;
        }
      }
      if (key === 'Enter') {
        setDetails(row.id);
        return true;
      }
      return false;
    },
    remove: (row) => {
      if (canEditRow(row)) {
        // Focus stays in the grid (the next row, else the previous one), so a second Delete never reaches the
        // canvas, which deletes the selected resource (code review C2).
        const index = shownRows.findIndex((r) => r.id === row.id);
        focusAfterRemoval(shownRows[index + 1] ?? shownRows[index - 1] ?? null);
        editing!.remove([{ resourceId: row.resourceId, key: row.name }]);
        toast.show({ tone: 'info', title: s.edit.removed(row.displayName) });
      }
    },
    copySecret: (row) => copySecretValue(row),
    expand: (row) => clickReveal(row, 'data-reveal-expand'),
    copyText: (row, column) => {
      if (column === 'variable') return row.displayName;
      if (column === 'nameInSource') return row.location.name;
      if (column === 'value') {
        const cell = cells.get(row.id);
        return cell ? copyableValue(row, cell) : null;
      }
      return null;
    },
    details: (row) => setDetails(row.id),
  };

  function editRow(row: VariableRow) {
    if (row.location.type === 'plain' || row.location.type === 'expression')
      setEditingCell({ rowId: row.id, column: 'value' });
    else setForm({ mode: 'change', rowId: row.id });
  }

  function rowMenu(row: VariableRow): MenuEntry[] {
    const entries: MenuEntry[] = [];
    const m = metadataOf(row);
    entries.push({
      kind: 'action',
      id: 'details',
      label: s.actions.details,
      hint: <span className="kbd">Alt ↵</span>,
      onSelect: () => setDetails(row.id),
    });
    if (m && row.location.capabilities.setValue === 'vault' && row.sync !== 'removed')
      entries.push({
        kind: 'action',
        id: 'set',
        label: s.actions.setValue,
        disabledReason: !canOperate
          ? strings.common.requiresRole('operator')
          : vaultReady
            ? undefined
            : s.setValue.vaultFirst,
        onSelect: () => openSetValue(m, row),
      });
    if (m && row.drift.includes('changed-outside'))
      entries.push({
        kind: 'action',
        id: 'accept',
        label: s.actions.acceptThis,
        disabledReason: canOperate ? undefined : strings.common.requiresRole('operator'),
        onSelect: () => void actions.accept(m.name),
      });
    if (m && canRecover(m, row.location))
      entries.push({
        kind: 'action',
        id: 'recover',
        label: s.actions.recover,
        disabledReason: !canOperate
          ? strings.common.requiresRole('operator')
          : actions.isRecovering(m.name)
            ? s.actions.recovering
            : undefined,
        onSelect: () => void actions.recover(m.name),
      });
    if (deployment && row.delivery?.refreshReferences)
      entries.push({
        kind: 'action',
        id: 'refresh-references',
        label: s.actions.refreshReferences,
        disabledReason: !canOperate
          ? strings.common.requiresRole('operator')
          : actions.isRefreshing(row.resourceId)
            ? s.actions.refreshingReferences
            : undefined,
        onSelect: () =>
          void actions.refreshReferences(row.resourceId, nowActionLabel(row.resourceId, row.delivery)),
      });
    if (isParameterRow(row))
      entries.push({
        kind: 'action',
        id: 'edit-value',
        label: s.actions.editValue,
        hint: <span className="kbd">F2</span>,
        disabledReason: canOperate ? undefined : strings.common.requiresRole('operator'),
        onSelect: () => editParameter(row),
      });
    if (m && deployment && context !== 'designer')
      entries.push({ kind: 'link', id: 'activity', label: s.actions.showActivity, to: activityHref(m.name) });
    if (m && row.drift.includes('tags-edited'))
      entries.push({
        kind: 'action',
        id: 'tags',
        label: s.actions.rewriteTags,
        icon: <Tags aria-hidden className="size-3.5" />,
        disabledReason: canOperate ? undefined : strings.common.requiresRole('operator'),
        onSelect: () => void actions.rewriteTags(),
      });
    if (row.editable && editing) {
      const reason = canDesign ? undefined : strings.common.requiresRole('designer');
      entries.push({ kind: 'separator', id: 'sep-edit' });
      if (!row.secret || row.location.type === 'secret')
        entries.push({
          kind: 'action',
          id: 'edit',
          label: s.actions.edit,
          hint: <span className="kbd">F2</span>,
          disabledReason: reason,
          onSelect: () => editRow(row),
        });
      entries.push({
        kind: 'action',
        id: 'change',
        label: s.actions.changeSource,
        disabledReason: reason,
        onSelect: () => setForm({ mode: 'change', rowId: row.id }),
      });
      entries.push({
        kind: 'action',
        id: 'rename',
        label: s.actions.rename,
        disabledReason: reason,
        onSelect: () => setEditingCell({ rowId: row.id, column: 'variable' }),
      });
      entries.push({
        kind: 'action',
        id: 'remove',
        label: s.actions.remove,
        hint: <span className="kbd">Del</span>,
        disabledReason: reason,
        onSelect: () => callbacks.remove(row),
      });
    } else if (
      !designMode &&
      context !== 'designer' &&
      !row.secret &&
      row.origin !== 'platform' &&
      row.origin !== 'trigger'
    ) {
      if (row.location.capabilities.setValue === 'deployment') {
        if (!isParameterRow(row)) entries.push({ kind: 'note', id: 'param', label: s.actions.parameterHint });
      } else if (canDesign)
        entries.push({
          kind: 'link',
          id: 'designer',
          label: s.actions.editInDesigner,
          to: designerHref(row),
        });
      else
        entries.push({
          kind: 'action',
          id: 'designer',
          label: s.actions.editInDesigner,
          disabledReason: strings.common.requiresRole('designer'),
          onSelect: () => {},
        });
    }
    entries.push({ kind: 'separator', id: 'sep-copy' });
    const cell = cells.get(row.id);
    const value = cell ? copyableValue(row, cell) : null;
    const secretTarget = revealTarget(cell);
    if (secretTarget)
      entries.push({
        kind: 'action',
        id: 'copy-secret',
        label: s.actions.copyValue,
        hint: <span className="kbd">Ctrl C</span>,
        disabledReason: canReveal ? undefined : deniedReason,
        onSelect: () => revealCopy.copy(secretTarget, valueCellOf(row)),
      });
    if (value !== null)
      entries.push({
        kind: 'action',
        id: 'copy-value',
        label: s.actions.copyValue,
        hint: <span className="kbd">Ctrl C</span>,
        onSelect: () => copy(value, s.actions.copyValue),
      });
    entries.push({
      kind: 'action',
      id: 'copy-name',
      label: s.actions.copyName,
      onSelect: () => copy(row.displayName, s.actions.copyName),
    });
    if (row.location.name)
      entries.push({
        kind: 'action',
        id: 'copy-source',
        label: s.actions.copyNameInSource,
        onSelect: () => copy(row.location.name!, s.actions.copyNameInSource),
      });
    if (row.location.consoleUrl)
      entries.push({
        kind: 'action',
        id: 'console',
        label: s.actions.openConsole,
        onSelect: () => window.open(row.location.consoleUrl!, '_blank', 'noopener'),
      });
    if (props.onSelectResource) {
      const target =
        row.location.type === 'output' || row.location.type === 'connection' ? row.location.container : null;
      if (row.connectionKey && props.onSelectConnection)
        entries.push({
          kind: 'action',
          id: 'go-connection',
          label: s.actions.goToConnection,
          onSelect: () => props.onSelectConnection!(row.connectionKey!),
        });
      entries.push({
        kind: 'action',
        id: 'go',
        label: s.actions.goToResource,
        onSelect: () => props.onSelectResource!(target ?? row.resourceId),
      });
    } else
      entries.push({
        kind: 'link',
        id: 'go',
        label: s.actions.goToResource,
        to: `/blueprints/${blueprintId}?${deployment ? `deployment=${deployment.id}&` : ''}select=${row.resourceId}`,
      });
    return entries;
  }

  // ---------------------------------------------------------------- inline editors and the form
  const takenNames = (resourceId: string) =>
    data.rows.filter((r) => r.resourceId === resourceId).map((r) => r.name);
  const override = (row: VariableRow, column: ColumnId): ReactNode | undefined => {
    if (parameterRow === row.id && column === 'value' && deployment && row.location.name)
      return (
        <ParameterEditor
          deployment={deployment}
          name={row.location.name}
          definition={props.parameters?.[row.location.name]}
          onDone={() => {
            setParameterRow(null);
            setFocusRequest({ rowId: row.id, nonce: Date.now() });
          }}
          onCancel={() => {
            setParameterRow(null);
            setFocusRequest({ rowId: row.id, nonce: Date.now() });
          }}
        />
      );
    if (!editingCell || editingCell.rowId !== row.id || !editing) return undefined;
    if (column === 'variable' && editingCell.column === 'variable')
      return (
        <InlineInput
          label={s.edit.rename(row.name)}
          initial={row.name}
          validate={(next) =>
            next === row.name
              ? null
              : !SETTING_NAME.test(next)
                ? s.edit.nameInvalid
                : takenNames(row.resourceId).includes(next)
                  ? s.edit.nameTaken
                  : null
          }
          onCommit={(next) => {
            setEditingCell(null);
            if (next !== row.name) editing.rename(row.resourceId, row.name, next);
            focusRow(`${row.resourceId}/${next}`);
          }}
          onCancel={() => {
            setEditingCell(null);
            focusRow(row.id);
          }}
        />
      );
    if (column === 'value' && editingCell.column === 'value')
      return (
        <InlineInput
          label={s.edit.editValue(row.name)}
          initial={row.expression ?? row.value ?? ''}
          expression
          onCommit={(next) => {
            setEditingCell(null);
            if (next !== (row.expression ?? row.value ?? ''))
              editing.setValue(row.resourceId, row.name, next);
            focusRow(row.id);
          }}
          onCancel={() => {
            setEditingCell(null);
            focusRow(row.id);
          }}
        />
      );
    return undefined;
  };
  const formRow =
    form && props.doc && editing ? (form.mode === 'change' ? rowById.get(form.rowId) : null) : null;
  const formNode =
    form && props.doc && editing ? (
      <VariableForm
        key={form.mode === 'change' ? form.rowId : 'add'}
        mode={form.mode}
        doc={props.doc}
        types={types}
        apps={form.mode === 'change' && formRow ? [formRow.resourceId] : scopeApps.map((a) => a.resourceId)}
        initial={
          formRow
            ? {
                resourceId: formRow.resourceId,
                name: formRow.name,
                value: formRow.expression ?? formRow.value ?? '',
              }
            : undefined
        }
        takenNames={takenNames}
        onSubmit={(edit) => {
          editing.set(edit);
          setForm(null);
          setFocusRequest({ rowId: `${edit.resourceId}/${edit.name}`, nonce: Date.now() });
        }}
        onCancel={() => setForm(null)}
      />
    ) : null;

  // ---------------------------------------------------------------- filters' menus
  const sourceTypesPresent = [...new Set(scopeRows.map((r) => r.location.type))];
  const statusesPresent = STATUS_ORDER.filter((id) =>
    scopeRows.some((r) => data.statuses.get(r.id)?.some((x) => x.id === id)),
  );
  const labelOfSource = (type: string) => {
    const row = scopeRows.find((r) => r.location.type === type);
    return row ? locationEntry(row.location).label(row.location) : type;
  };
  const radio = <T,>(
    id: string,
    label: string,
    checked: boolean,
    value: T,
    set: (value: T) => void,
  ): MenuEntry => ({
    kind: 'radio',
    id,
    label,
    checked,
    onSelect: () => set(value),
  });
  const setFilter =
    <K extends keyof Filters>(key: K) =>
    (value: Filters[K]) =>
      setFilters((f) => ({ ...f, [key]: value }));
  const sourceEntries: MenuEntry[] = [
    radio('all', s.filter.all, !filters.source, null, setFilter('source')),
    ...sourceTypesPresent.map((type) =>
      radio(type, labelOfSource(type), filters.source === type, type, setFilter('source')),
    ),
  ];
  const statusEntries: MenuEntry[] = [
    radio('all', s.filter.all, !filters.status, null, setFilter('status')),
    radio('issues', s.filter.issues, filters.status === 'issues', 'issues' as const, setFilter('status')),
    ...statusesPresent.map((id) =>
      radio(id, s.status[id as StatusId], filters.status === id, id, setFilter('status')),
    ),
  ];
  const kindEntries: MenuEntry[] = [
    radio('all', s.filter.all, !filters.kind, null, setFilter('kind')),
    ...(['declared', 'system', 'generated', 'not-secret'] as KindFilter[]).map((kind) =>
      radio(
        kind,
        kind === 'not-secret' ? s.filter.notSecret : s.kinds[kind],
        filters.kind === kind,
        kind,
        setFilter('kind'),
      ),
    ),
  ];
  const groupEntries: MenuEntry[] = (['resource', 'source', 'none'] as GroupBy[]).map((g) =>
    radio(g, s.filter.groupBy[g], group === g, g, (value) => updateSettings((v) => ({ ...v, group: value }))),
  );
  const columnEntries: MenuEntry[] = offeredColumns({ rotation: rotationInScope }).map((column) => ({
    kind: 'check',
    id: column,
    label: s.columns[column],
    checked: settings.columns.includes(column),
    onSelect: () =>
      updateSettings((v) => ({
        ...v,
        columns: v.columns.includes(column) ? v.columns.filter((c) => c !== column) : [...v.columns, column],
      })),
  }));

  // ---------------------------------------------------------------- scope chip
  const scopeLabel = !scopeIds
    ? s.scope.all
    : scopeIds.length === 1
      ? scopeIds[0]
      : s.scope.several(scopeIds.length);
  const scopeSuffix = deployment
    ? s.scope.inDeployment(deployment.name)
    : s.scope.design(props.designVersion ?? null);
  const scopeEntries: MenuEntry[] = [
    {
      kind: 'radio',
      id: '__all',
      label: s.scope.allItem,
      checked: !scopeIds,
      onSelect: () => props.onScopeChange({ pinned: context === 'designer', ids: null }),
    },
    ...data.apps.map<MenuEntry>((app) => ({
      kind: 'radio',
      id: app.resourceId,
      label: (
        <span className="flex gap-2">
          <span className="font-mono">{app.resourceId}</span>
          <span className="text-fg-muted">{types.get(app.type)?.displayName ?? app.type}</span>
        </span>
      ),
      checked: scopeIds?.length === 1 && scopeIds[0] === app.resourceId,
      onSelect: () => props.onScopeChange({ pinned: true, ids: [app.resourceId] }),
    })),
  ];

  // ---------------------------------------------------------------- secrets view data
  const allSecrets = overview.data?.secrets ?? [];
  const secretWords = filters.search.toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const visibleSecrets = allSecrets.filter((secret) => {
    if (filters.resource && !secret.usedBy.some((u) => u.resourceId === filters.resource)) return false;
    const text =
      `${secret.name} ${secret.location?.name ?? ''} ${secret.description ?? ''}`.toLocaleLowerCase();
    return secretWords.every((w) => text.includes(w));
  });
  const deliveryFor = (name: string) =>
    data.rows.find((r) => r.secretName?.toLowerCase() === name.toLowerCase() && r.delivery)?.delivery ?? null;
  const secretActions = {
    primary: (secret: SecretMetadata) => {
      if (secret.drift.includes('changed-outside')) return acceptButton(secret.name);
      if (canRecover(secret)) return recoverButton(secret, true);
      if (secret.kind === 'unmanaged' && !secret.ignored)
        return (
          <ActionButton
            permission="operate"
            className="btn-sm"
            tabIndex={-1}
            aria-label={`${s.actions.ignore}: ${secret.name}`}
            onClick={() => void actions.ignore(secret.name)}
          >
            {s.actions.ignore}
          </ActionButton>
        );
      if (secret.drift.includes('tags-edited'))
        return (
          <ActionButton
            permission="operate"
            className="btn-sm"
            tabIndex={-1}
            aria-label={`${s.actions.rewriteTags}: ${secret.name}`}
            onClick={() => void actions.rewriteTags()}
          >
            {s.actions.rewriteTags}
          </ActionButton>
        );
      if (secret.kind === 'declared' && secret.location?.capabilities.setValue === 'vault')
        return setValueButton(
          secret,
          null,
          secret.drift.includes('missing-value') || secret.drift.includes('deleted-outside'),
        );
      return null;
    },
    menu: (secret: SecretMetadata): MenuEntry[] => {
      const entries: MenuEntry[] = [
        {
          kind: 'action',
          id: 'details',
          label: s.actions.details,
          onSelect: () => setDetails(`secret:${secret.name}`),
        },
      ];
      if (secret.kind === 'declared' && secret.location?.capabilities.setValue === 'vault')
        entries.push({
          kind: 'action',
          id: 'set',
          label: s.actions.setValue,
          disabledReason: !canOperate
            ? strings.common.requiresRole('operator')
            : vaultReady
              ? undefined
              : s.setValue.vaultFirst,
          onSelect: () => openSetValue(secret, null),
        });
      if (secret.drift.includes('changed-outside'))
        entries.push({
          kind: 'action',
          id: 'accept',
          label: s.actions.acceptThis,
          disabledReason: canOperate ? undefined : strings.common.requiresRole('operator'),
          onSelect: () => void actions.accept(secret.name),
        });
      if (canRecover(secret))
        entries.push({
          kind: 'action',
          id: 'recover',
          label: s.actions.recover,
          disabledReason: !canOperate
            ? strings.common.requiresRole('operator')
            : actions.isRecovering(secret.name)
              ? s.actions.recovering
              : undefined,
          onSelect: () => void actions.recover(secret.name),
        });
      if (deployment && context !== 'designer' && secret.kind !== 'unmanaged')
        entries.push({
          kind: 'link',
          id: 'activity',
          label: s.actions.showActivity,
          to: activityHref(secret.name),
        });
      if (secret.drift.includes('tags-edited'))
        entries.push({
          kind: 'action',
          id: 'tags',
          label: s.actions.rewriteTags,
          disabledReason: canOperate ? undefined : strings.common.requiresRole('operator'),
          onSelect: () => void actions.rewriteTags(),
        });
      if (secret.kind === 'unmanaged' && !secret.ignored) {
        entries.push({
          kind: 'action',
          id: 'ignore',
          label: s.actions.ignore,
          disabledReason: canOperate ? undefined : strings.common.requiresRole('operator'),
          onSelect: () => void actions.ignore(secret.name),
        });
        if (props.onAddDeclaration && editing)
          entries.push({
            kind: 'action',
            id: 'declare',
            label: s.actions.addDeclaration,
            disabledReason: canDesign ? undefined : strings.common.requiresRole('designer'),
            onSelect: () => props.onAddDeclaration!(secret.name),
          });
        else if (deployment)
          entries.push({
            kind: 'link',
            id: 'declare',
            label: s.actions.addDeclaration,
            to: `/blueprints/${blueprintId}?deployment=${deployment.id}&select=__vault__`,
          });
      }
      entries.push({ kind: 'separator', id: 'sep' });
      const secretTarget = secret.location
        ? revealTarget(secretValueCell(secret, secret.location, secret.kind, valueContext))
        : null;
      if (secretTarget)
        entries.push({
          kind: 'action',
          id: 'copy-secret',
          label: s.actions.copyValue,
          hint: <span className="kbd">Ctrl C</span>,
          disabledReason: canReveal ? undefined : deniedReason,
          onSelect: () => revealCopy.copy(secretTarget, secretValueCellOf(secret.name)),
        });
      entries.push({
        kind: 'action',
        id: 'copy-name',
        label: s.actions.copyName,
        onSelect: () => copy(secret.name, s.actions.copyName),
      });
      if (secret.location?.name && secret.location.name !== secret.name)
        entries.push({
          kind: 'action',
          id: 'copy-source',
          label: s.actions.copyNameInSource,
          onSelect: () => copy(secret.location!.name!, s.actions.copyNameInSource),
        });
      if (secret.location?.consoleUrl)
        entries.push({
          kind: 'action',
          id: 'console',
          label: s.actions.openConsole,
          onSelect: () => window.open(secret.location!.consoleUrl!, '_blank', 'noopener'),
        });
      return entries;
    },
  };

  // ---------------------------------------------------------------- bulk
  const selectedRows = shownRows.filter((r) => selected.has(r.id));
  const selectedSecrets = visibleSecrets.filter((x) => selected.has(x.name));
  const outsideNames =
    view === 'variables'
      ? [
          ...new Set(
            selectedRows
              .filter((r) => r.drift.includes('changed-outside') && r.metadata)
              .map((r) => r.metadata!.name),
          ),
        ]
      : selectedSecrets.filter((x) => x.drift.includes('changed-outside')).map((x) => x.name);
  const tagsEdited =
    view === 'variables'
      ? selectedRows.some((r) => r.drift.includes('tags-edited'))
      : selectedSecrets.some((x) => x.drift.includes('tags-edited'));
  const unmanaged =
    view === 'secrets'
      ? selectedSecrets.filter((x) => x.kind === 'unmanaged' && !x.ignored).map((x) => x.name)
      : [];
  const removable = view === 'variables' && editable ? selectedRows.filter((r) => r.editable) : [];
  const selectedCount = view === 'variables' ? selectedRows.length : selectedSecrets.length;

  // ---------------------------------------------------------------- details
  const detailsRow = details && !details.startsWith('secret:') ? (rowById.get(details) ?? null) : null;
  const detailsSecret = details?.startsWith('secret:')
    ? (allSecrets.find((x) => `secret:${x.name}` === details) ?? null)
    : null;
  const detailsActions = (secret: SecretMetadata | null, row: VariableRow | null): ReactNode => {
    if (!secret) return null;
    return (
      <>
        {secret.drift.includes('changed-outside') && acceptButton(secret.name)}
        {canRecover(secret, row?.location) && recoverButton(secret)}
        {secret.kind === 'declared' &&
          secret.location?.capabilities.setValue === 'vault' &&
          setValueButton(secret, row)}
      </>
    );
  };

  // ---------------------------------------------------------------- render
  const strips: ReactNode[] = [];
  if (context === 'run' && deployment)
    strips.push(
      <p key="run" className="var-strip tone-info">
        {s.strips.runSnapshot(deployment.name)}
      </p>,
    );
  if (!canWrite)
    strips.push(
      <p key="reader" className="var-strip tone-neutral">
        {s.toolbar.readOnly}
      </p>,
    );
  if (variables?.designError)
    strips.push(
      <p key="design" className="var-strip tone-info">
        {s.strips.designError(deployment?.name ?? '', variables.designError.message.replace(/\.$/, ''))}
      </p>,
    );
  if (variables?.listingError)
    strips.push(
      <div key="listing" role="status" className="var-strip tone-warning" data-testid="listing-error">
        <span>
          {variables.listingError.code === 'VAULT_THROTTLED' && variables.listedAt
            ? s.strips.throttled(formatRelative(variables.listedAt))
            : `${s.strips.listing(variables.listingError.code, variables.listingError.message)} ${variables.stale && variables.listedAt ? s.strips.staleSince(formatRelative(variables.listedAt)) : s.strips.noListing}`}
        </span>
        <button type="button" className="btn btn-sm" disabled={refreshing} onClick={() => void refresh()}>
          {s.actions.retry}
        </button>
      </div>,
    );
  const namespaceEntry = managedVaultEntry(vault?.provider);
  if (view === 'variables' && vault?.status === 'not-created' && deployment)
    strips.push(
      <div key="vault" className="var-strip tone-warning" data-testid="vault-banner">
        <span>
          {namespaceEntry?.prepareText
            ? namespaceEntry.prepareText(
                deployment.name,
                vault.name ?? '',
                vault.region ?? '',
                vault.readIdentity?.cloudAccountName ?? '',
              )
            : s.strips.vaultNotCreated(deployment.name)}
        </span>
        <ActionButton
          permission="operate"
          className="btn-sm"
          disabledReason={runActive ?? (actions.busy ? strings.common.loading : null)}
          icon={<Vault aria-hidden className="size-3.5" />}
          onClick={() => void actions.prepareVault()}
        >
          {s.actions.prepareVault}
        </ActionButton>
      </div>,
    );
  if (view === 'variables' && vault?.status === 'store' && deployment && vault.modeChange !== 'to-managed')
    strips.push(
      <p key="store" className="var-strip tone-warning" data-testid="store-banner">
        {s.strips.store(deployment.name, vault.store?.name ?? '')}
      </p>,
    );
  // Feature spec 15 19.7: a vault-mode switch the next apply completes.
  if (view === 'variables' && deployment && vault?.modeChange === 'to-managed') {
    const declared = (variables?.secrets ?? []).filter((m) => m.kind === 'declared');
    const set = declared.filter((m) => m.hasValue).length;
    const label = namespaceEntry?.label({ label: '', container: null }) ?? vault.label ?? '';
    strips.push(
      <div key="to-managed" className="var-strip tone-info" data-testid="switch-to-managed">
        <span className="min-w-0 flex-1">
          {strings.vaults.toManaged(
            deployment.name,
            label,
            vault.store?.name ?? deployment.settings.secretStore,
          )}
          <ol className="mt-1 list-decimal pl-5">
            <li>
              {strings.vaults.toManagedSteps.prepare}
              {vault.name ? ' ✓' : ''}
            </li>
            <li>{strings.vaults.toManagedSteps.values(set, declared.length)}</li>
            <li>{strings.vaults.toManagedSteps.apply}</li>
          </ol>
        </span>
      </div>,
    );
  }
  if (view === 'variables' && deployment && vault?.modeChange === 'to-store') {
    const declared = (variables?.secrets ?? []).filter((m) => m.kind === 'declared' && m.hasValue).length;
    const days = vault.deletedRetentionDays ?? 0;
    const until = days > 0 ? formatDateTime(new Date(openedAt + days * 86_400_000).toISOString()) : null;
    const label = namespaceEntry?.label({ label: '', container: null }) ?? vault.label ?? '';
    strips.push(
      <p
        key="to-store"
        className={`var-strip ${until ? 'tone-info' : 'tone-danger'}`}
        data-testid="switch-to-store"
      >
        {strings.vaults.toStore(deployment.name, deployment.settings.secretStore, declared, label, until)}
      </p>,
    );
  }
  // Feature spec 15 19.3: one strip per per-app app when the table isn't grouped by app.
  if (view === 'variables' && deployment && group !== 'resource')
    for (const app of scopeApps) {
      const found = appDelivery(app, data.rows);
      if (!found || (!app.appReferenceStatus && found.delivery.referenceStatus !== 'per-app')) continue;
      strips.push(
        <AppStrip
          key={`app:${app.resourceId}`}
          app={app}
          delivery={found.delivery}
          entry={found.entry}
          checkedAt={app.referenceStatusCheckedAt}
          stillRollingOut={pollExpired && app.appReferenceStatus?.neutral === 'pending'}
          action={rolloutButton(app)}
          onDetails={() => openAppDetails(app.resourceId)}
        />,
      );
    }
  if (view === 'variables' && deployment?.status === 'failed' && deployment.lastSuccessfulApply)
    strips.push(
      <p key="failed" className="var-strip tone-warning">
        {s.strips.lastApplyFailed(variables?.designVersion ?? null)}
      </p>,
    );
  if (view === 'variables' && deployment)
    for (const app of scopeApps)
      if (!app.applied && variables)
        strips.push(
          <p key={`na:${app.resourceId}`} className="var-strip tone-info">
            {s.strips.notDeployed(app.resourceId, deployment.name)}
          </p>,
        );
  if (view === 'variables' && deployment)
    for (const app of scopeApps) {
      const added = app.settingsDrift?.entries.filter((e) => e.change === 'added') ?? [];
      if (added.length)
        strips.push(
          <p key={`cloud:${app.resourceId}`} className="var-strip tone-warning" data-testid="cloud-added">
            {s.strips.cloudAdded(
              app.resourceId,
              added.map((e) => e.name).join(', '),
              formatRelative(app.settingsDrift!.checkedAt),
            )}
          </p>,
        );
    }
  if (view === 'variables' && group === 'none')
    for (const app of scopeApps)
      if (app.referenceStatusError)
        strips.push(
          <p key={`rs:${app.resourceId}`} className="var-strip tone-warning">
            {s.strips.referenceStatusError(app.referenceStatusError)}
          </p>,
        );

  const listedAt = view === 'secrets' ? overview.data?.vault.listedAt : variables?.listedAt;
  let body: ReactNode;
  const showDeclarations = view === 'secrets' && !!props.doc && (designMode || declarationsMode);
  if (showDeclarations) {
    body = (
      <div className="flex min-h-0 flex-1 flex-col">
        {deployment && (
          <div className="var-vault-strip">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeclarationsMode(false)}>
              <ArrowLeft aria-hidden className="size-4" />
              {strings.declarations.back(deployment.name)}
            </button>
          </div>
        )}
        <DeclarationsEditor
          doc={props.doc!}
          designRows={data.rows}
          diagnostics={props.secretDiagnostics}
          editing={canDesign ? (props.declarations ?? null) : null}
          focus={props.pendingDeclaration?.name ? props.pendingDeclaration : null}
        />
      </div>
    );
  } else if (view === 'secrets') {
    body = (
      <SecretsView
        deploymentName={deployment?.name ?? null}
        overview={overview.data ?? null}
        visible={visibleSecrets}
        stripAction={
          props.declarations !== undefined && props.doc ? (
            <button type="button" className="btn btn-sm" onClick={() => setDeclarationsMode(true)}>
              <Pencil aria-hidden className="size-3.5" />
              {strings.declarations.edit}
            </button>
          ) : null
        }
        valueContext={valueContext}
        selected={selected}
        highlighted={props.highlightedSecret ?? null}
        narrow={narrow}
        refreshing={refreshing}
        canSelect={canOperate}
        actions={secretActions}
        onRefresh={() => void refresh()}
        onPrepare={() => void actions.prepareVault()}
        prepareDisabled={runActive ?? (actions.busy ? strings.common.loading : null)}
        onSelect={(name, mode) =>
          select(
            name,
            mode,
            visibleSecrets.map((x) => x.name),
          )
        }
        onSelectAll={() => setSelected(new Set(visibleSecrets.map((x) => x.name)))}
        onClearSelection={() => setSelected(new Set())}
        onHighlight={(name) => props.onHighlightSecret?.(name)}
        onDetails={(name) => setDetails(`secret:${name}`)}
        onCopyValue={copySecretRowValue}
        usedByChip={(resourceId, settingNames) =>
          props.onSelectResource ? (
            <button
              key={resourceId}
              type="button"
              tabIndex={-1}
              className="badge tone-neutral font-mono hover:underline"
              title={settingNames.join(', ')}
              aria-label={strings.secrets.selectResource(resourceId)}
              onClick={() => props.onSelectResource!(resourceId)}
            >
              {resourceId}
            </button>
          ) : (
            <Link
              key={resourceId}
              tabIndex={-1}
              className="badge tone-neutral font-mono hover:underline"
              title={settingNames.join(', ')}
              to={`/blueprints/${blueprintId}?${deployment ? `deployment=${deployment.id}&` : ''}select=${resourceId}`}
            >
              {resourceId}
            </Link>
          )
        }
      />
    );
    if (deployment && overview.error)
      body = <ErrorMessage error={overview.error} onRetry={() => void overview.refetch()} />;
    else if (deployment && !overview.data) body = <SkeletonRows />;
  } else if (data.error) {
    body = (
      <ErrorMessage
        error={data.error}
        onRetry={() =>
          void queryClient.invalidateQueries({ queryKey: queryKeys.variables(deployment?.id ?? '') })
        }
      />
    );
  } else if (data.loading) {
    body = <SkeletonRows />;
  } else if (scopeApps.length === 0) {
    body = <p className="var-empty">{data.apps.length === 0 ? s.empty.noApps : s.empty.none}</p>;
  } else if (scopeRows.length === 0 && !form) {
    body = (
      <div className="var-empty">
        <p>{scopeApps.length === 1 ? s.empty.app(scopeApps[0].resourceId) : s.empty.none}</p>
        {editable ? (
          <button type="button" className="btn mt-2" onClick={() => setForm({ mode: 'add' })}>
            <Plus aria-hidden className="size-4" />
            {s.toolbar.addVariable}
          </button>
        ) : (
          <p className="help">{s.empty.connections}</p>
        )}
      </div>
    );
  } else if (visibleRows.length === 0 && !form) {
    body = (
      <div className="var-empty">
        <p>{s.table.noRows}</p>
        <button type="button" className="btn mt-2" onClick={() => setFilters({ ...NO_FILTERS })}>
          {s.toolbar.clearFilters}
        </button>
      </div>
    );
  } else {
    body = (
      <VariablesTable
        lines={lines}
        columns={narrow ? ['variable'] : columns}
        settings={settings}
        statuses={data.statuses}
        cells={cells}
        selected={selected}
        layout={layout}
        label={s.table.label}
        typeLabel={(type) => types.get(type)?.displayName ?? type}
        callbacks={callbacks}
        override={override}
        inserted={formNode ? { after: form?.mode === 'change' ? form.rowId : '', node: formNode } : null}
        focusRequest={focusRequest}
        onFocusHandled={clearFocusRequest}
        onActiveRow={(rowId) => {
          // An open details pane follows the row the user moves to (spec 6.10.8, code review C22).
          if (detailsRow && !narrow && rowId !== detailsRow.id) setDetails(rowId);
        }}
        groupAction={groupAction}
        groupMenu={groupMenu}
        groupStatus={groupStatus}
        onAppDetails={(app) => {
          if (appDelivery(app, data.rows)) openAppDetails(app.resourceId);
        }}
        onSort={(column) => updateSettings((v) => ({ ...v, sort: nextSort(v.sort, column) }))}
        onMoveColumn={(column, delta) => updateSettings((v) => moveColumn(v, column, delta))}
        onToggleGroup={(key) => {
          const next = new Set(collapsed);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          setCollapsed(next);
        }}
        onSelect={(id, mode) => select(id, mode)}
        onSelectAll={() => setSelected(new Set(shownRows.map((r) => r.id)))}
        onClearSelection={() => setSelected(new Set())}
      />
    );
    if (narrow && formNode)
      body = (
        <>
          {formNode}
          {body}
        </>
      );
  }

  const detailsApp = detailsRow ? data.apps.find((a) => a.resourceId === detailsRow.resourceId) : undefined;
  const detailsAppStatus =
    detailsApp?.appReferenceStatus && detailsRow?.delivery?.referenceStatus === 'per-app' ? (
      <>
        <span>{strings.vaults.appStatusOf(detailsApp.resourceId)}</span>
        <AppStatusChip app={detailsApp} entry={deliveryEntry(detailsRow.delivery)} />
        {appStatusParts(detailsApp, detailsRow.delivery, detailsApp.referenceStatusCheckedAt).map((part) => (
          <span key={part} className="text-fg-muted">
            · {part}
          </span>
        ))}
      </>
    ) : null;
  const appDetailsApp =
    appDetails && !detailsRow && !detailsSecret
      ? data.apps.find((a) => a.resourceId === appDetails)
      : undefined;
  const appDetailsFound = appDetailsApp ? appDelivery(appDetailsApp, data.rows) : null;
  const appDetailsPane = appDetailsApp ? (
    <AppDetailsPane
      app={appDetailsApp}
      delivery={appDetailsFound?.delivery ?? null}
      entry={appDetailsFound?.entry ?? null}
      rows={data.rows.filter((r) => r.resourceId === appDetailsApp.resourceId)}
      checkedAt={appDetailsApp.referenceStatusCheckedAt}
      rollout={rolloutButton(appDetailsApp, true)}
      narrow={narrow}
      fullscreen={layout === 'cards'}
      onOpenRow={(rowId) => setDetails(rowId)}
      onClose={() => setAppDetails(null)}
    />
  ) : null;
  const detailsOpen = !!(detailsRow || detailsSecret);
  const detailsPane = detailsOpen ? (
    <DetailsPane
      row={detailsRow}
      secret={detailsSecret}
      cell={
        detailsRow
          ? (cells.get(detailsRow.id) ?? valueCell(detailsRow, valueContext))
          : detailsSecret?.location
            ? secretValueCell(detailsSecret, detailsSecret.location, detailsSecret.kind, valueContext)
            : { kind: 'unknown', label: s.value.label.unknown }
      }
      statuses={
        detailsRow
          ? (data.statuses.get(detailsRow.id) ?? [])
          : detailsSecret
            ? secretStatuses(detailsSecret, data.valueContext.listingFailed)
            : []
      }
      actions={detailsActions(detailsRow?.metadata ?? detailsSecret, detailsRow)}
      deliveryAction={
        detailsRow?.delivery?.refreshReferences && deployment
          ? refreshButton(detailsRow.resourceId, detailsRow.delivery)
          : null
      }
      activity={
        deployment && (detailsRow?.metadata ?? detailsSecret) ? (
          <RecentActivity
            deploymentId={deployment.id}
            secretName={(detailsRow?.metadata ?? detailsSecret)!.name}
            showAllHref={activityHref((detailsRow?.metadata ?? detailsSecret)!.name)}
          />
        ) : null
      }
      lastApply={deployment?.lastSuccessfulApply ?? null}
      appliedVersion={variables?.appliedVersion ?? null}
      referenceStatusCheckedAt={
        detailsRow
          ? (data.apps.find((a) => a.resourceId === detailsRow.resourceId)?.referenceStatusCheckedAt ?? null)
          : null
      }
      appStatus={detailsAppStatus}
      declaration={
        props.doc && (detailsRow?.secretName ?? detailsSecret?.name)
          ? (props.doc.secrets?.[(detailsRow?.secretName ?? detailsSecret?.name)!] ?? null)
          : null
      }
      narrow={narrow}
      fullscreen={layout === 'cards'}
      onClose={() => {
        const rowId = detailsRow?.id;
        setDetails(null);
        // Back from the details that replaced the list: focus returns to the row.
        if (rowId) focusRow(rowId);
      }}
    />
  ) : null;

  return (
    <RevealContext.Provider value={revealEnv}>
      <div
        ref={setRoot}
        className="var-window"
        data-layout={layout}
        onKeyDown={(event) => {
          const target = event.target as HTMLElement;
          if (
            event.key === '/' &&
            !/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) &&
            !target.isContentEditable
          ) {
            event.preventDefault();
            search.current?.focus();
          }
        }}
      >
        <div className="tw-toolbar var-toolbar">
          <span className="var-scope">
            <span className="text-fg-muted">{s.scope.label}</span>
            <MenuButton
              portal
              buttonClassName="var-scope-button"
              buttonLabel={`${s.scope.label}: ${scopeLabel} ${scopeSuffix}`}
              buttonContent={
                <>
                  <Globe aria-hidden className="size-3.5" />
                  <span className="font-semibold">{scopeLabel}</span>
                  <span className="text-fg-muted">{scopeSuffix}</span>
                  <ChevronDown aria-hidden className="size-3" />
                </>
              }
              menuLabel={s.scope.menu}
              entries={scopeEntries}
            />
            {context === 'designer' && (
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                aria-pressed={props.scope.pinned}
                aria-label={props.scope.pinned ? s.scope.unpin : s.scope.pin}
                title={props.scope.pinned ? s.scope.pinnedHint : s.scope.pin}
                onClick={() =>
                  props.onScopeChange(
                    props.scope.pinned ? { pinned: false, ids: null } : { pinned: true, ids: scopeIds },
                  )
                }
              >
                {props.scope.pinned ? (
                  <Pin aria-hidden className="size-4 fill-current" />
                ) : (
                  <PinOff aria-hidden className="size-4" />
                )}
              </button>
            )}
          </span>
          <div role="group" aria-label={s.views.label} className="segmented">
            {(['variables', 'secrets'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                className="segmented-item"
                onClick={() => {
                  setDetails(null);
                  updateSettings((current) => ({ ...current, view: v }));
                }}
              >
                {s.views[v]}
              </button>
            ))}
          </div>
          <input
            ref={search}
            type="search"
            className="input var-search"
            aria-label={s.toolbar.search}
            aria-keyshortcuts="/"
            placeholder={s.toolbar.search}
            value={filters.search}
            onChange={(event) => setFilters((f) => ({ ...f, search: event.target.value }))}
          />
          {view === 'variables' && (
            <>
              <MenuButton
                portal
                buttonClassName="var-chip"
                buttonContent={chip(
                  s.filter.source,
                  filters.source ? labelOfSource(filters.source) : s.filter.all,
                  !!filters.source,
                )}
                menuLabel={s.filter.source}
                entries={sourceEntries}
              />
              <MenuButton
                portal
                buttonClassName="var-chip"
                buttonContent={chip(
                  s.filter.status,
                  filters.status === 'issues'
                    ? s.filter.issues
                    : filters.status
                      ? s.status[filters.status]
                      : s.filter.all,
                  !!filters.status,
                )}
                menuLabel={s.filter.status}
                entries={statusEntries}
              />
              {width >= 1300 && (
                <MenuButton
                  portal
                  buttonClassName="var-chip"
                  buttonContent={chip(
                    s.filter.kind,
                    filters.kind
                      ? filters.kind === 'not-secret'
                        ? s.filter.notSecret
                        : s.kinds[filters.kind]
                      : s.filter.all,
                    !!filters.kind,
                  )}
                  menuLabel={s.filter.kind}
                  entries={kindEntries}
                />
              )}
              {scopeApps.length > 1 && (
                <MenuButton
                  portal
                  buttonClassName="var-chip"
                  buttonContent={
                    <>
                      <Group aria-hidden className="size-3.5" />
                      <span>{s.filter.chip(s.filter.group, s.filter.groupBy[group])}</span>
                      <ChevronDown aria-hidden className="size-3" />
                    </>
                  }
                  menuLabel={s.filter.group}
                  entries={groupEntries}
                />
              )}
            </>
          )}
          {props.runResources && view === 'variables' && filters.inRun && (
            <button
              type="button"
              className="var-chip is-active"
              aria-label={s.filter.removeInRun}
              title={s.filter.removeInRun}
              onClick={() => setFilters((f) => ({ ...f, inRun: false }))}
            >
              {s.filter.inRun} ×
            </button>
          )}
          {deployment && !revealOn && (
            <span
              className="var-hidden-chip"
              title={s.toolbar.secretValuesHiddenHelp}
              tabIndex={0}
              aria-label={`${s.toolbar.secretValuesHidden}. ${s.toolbar.secretValuesHiddenHelp}`}
            >
              <Lock aria-hidden className="size-3.5" />
              {s.toolbar.secretValuesHidden}
            </span>
          )}
          <span className="var-toolbar-end">
            {deployment && listedAt && (
              <span className="text-[12px] text-fg-muted" title={formatDateTime(listedAt)}>
                {s.toolbar.listedAgo(formatRelative(listedAt))}
              </span>
            )}
            {deployment && (
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                aria-label={refreshing ? s.toolbar.refreshing : s.toolbar.refresh}
                title={s.toolbar.refresh}
                disabled={refreshing}
                onClick={() => void refresh()}
              >
                <RefreshCw aria-hidden className={`size-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
            {view === 'variables' && layout === 'table' && (
              <MenuButton
                portal
                placement="below-end"
                buttonClassName="btn btn-ghost btn-icon"
                buttonLabel={s.toolbar.columns}
                buttonTitle={s.toolbar.columns}
                buttonContent={<Columns3 aria-hidden className="size-4" />}
                menuLabel={s.toolbar.columns}
                entries={columnEntries}
              />
            )}
            <MenuButton
              portal
              placement="below-end"
              buttonClassName="btn btn-ghost btn-icon"
              buttonLabel={s.toolbar.more}
              buttonTitle={s.toolbar.more}
              buttonContent={<Ellipsis aria-hidden className="size-4" />}
              menuLabel={s.toolbar.more}
              entries={[
                {
                  kind: 'action',
                  id: 'types',
                  label: s.toolbar.sourceTypes,
                  onSelect: () => setSourceTypes(true),
                },
              ]}
            />
            {editing && view === 'variables' && (
              <ActionButton
                permission="design"
                className="btn-sm"
                icon={<Plus aria-hidden className="size-4" />}
                disabledReason={scopeApps.length === 0 ? s.empty.noApps : null}
                onClick={() => setForm({ mode: 'add' })}
              >
                {s.toolbar.addVariable}
              </ActionButton>
            )}
          </span>
        </div>
        {strips}
        {selectedCount > 0 && (
          <div role="region" aria-label={s.bulk.label} className="var-bulk">
            <span className="font-semibold">{s.bulk.selected(selectedCount)}</span>
            {outsideNames.length > 0 && (
              <ActionButton
                permission="operate"
                className="btn-sm"
                disabledReason={actions.busy ? strings.common.loading : null}
                onClick={() => void actions.bulk(outsideNames, 'accept')}
              >
                {s.bulk.accept(outsideNames.length)}
              </ActionButton>
            )}
            {tagsEdited && (
              <ActionButton
                permission="operate"
                className="btn-sm"
                onClick={() => void actions.rewriteTags()}
              >
                {s.bulk.rewriteTags}
              </ActionButton>
            )}
            {unmanaged.length > 0 && (
              <ActionButton
                permission="operate"
                className="btn-sm"
                disabledReason={actions.busy ? strings.common.loading : null}
                onClick={() => void actions.bulk(unmanaged, 'ignore')}
              >
                {s.bulk.ignore(unmanaged.length)}
              </ActionButton>
            )}
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                const names =
                  view === 'variables'
                    ? selectedRows.map((r) => r.displayName)
                    : selectedSecrets.map((x) => x.name);
                copy(names.join('\n'), s.bulk.copyNames);
              }}
            >
              {s.bulk.copyNames}
            </button>
            {removable.length > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => {
                  const gone = new Set(removable.map((r) => r.id));
                  focusAfterRemoval(shownRows.find((r) => !gone.has(r.id)) ?? null);
                  editing!.remove(removable.map((r) => ({ resourceId: r.resourceId, key: r.name })));
                  setSelected(new Set());
                }}
              >
                {s.bulk.remove(removable.length)}
              </button>
            )}
            <span role="status" className="text-[12px] text-fg-muted">
              {actions.progress ?? ''}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm ml-auto"
              onClick={() => setSelected(new Set())}
            >
              {s.bulk.clear}
            </button>
          </div>
        )}
        {data.fetching && !data.loading && <div className="var-progress" aria-hidden />}
        <div className="var-body">
          {!((detailsOpen || appDetailsPane) && narrow) && <div className="var-main">{body}</div>}
          {detailsPane ?? appDetailsPane}
        </div>
        {popover && deployment && (
          <SetValuePopover
            deploymentId={deployment.id}
            secret={popover.secret}
            location={popover.row?.location ?? popover.secret.location ?? null}
            delivery={popover.row?.delivery ?? deliveryFor(popover.secret.name)}
            anchor={popover.anchor}
            onClose={() => setPopover(null)}
            runActive={!!deployment.activeRunId}
            deploymentName={deployment.name}
          />
        )}
        {sourceTypes && <SourceTypesDialog onClose={() => setSourceTypes(false)} />}
        {confirmRestart && (
          <ConfirmDialog
            title={strings.vaults.confirmRestart(confirmRestart.app)}
            message={confirmRestart.disruption}
            confirmLabel={strings.vaults.confirmRestart(confirmRestart.app)}
            busy={actions.isRestarting(confirmRestart.app)}
            onClose={() => setConfirmRestart(null)}
            onConfirm={() => {
              const target = confirmRestart;
              setConfirmRestart(null);
              const found = appDelivery({ resourceId: target.app }, data.rows);
              void actions.restartApp(target.app, {
                done: found?.entry.nowActionDoneText?.([target.app]) ?? target.label,
                failed: (error) => found?.entry.nowActionFailedText?.(target.app, error) ?? error,
              });
            }}
          />
        )}
        {revealCopy.popover}
      </div>
    </RevealContext.Provider>
  );
}

function SkeletonRows() {
  return (
    <div className="var-skeleton" role="status" aria-busy="true">
      <span className="sr-only">{strings.common.loading}</span>
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="var-skeleton-row" />
      ))}
    </div>
  );
}
